<template>
    <div>
        <div class="px-1 pb-50 text-muted small">
            <template v-if="loading">Загружаются точки замеров…</template>
            <template v-else-if="error">{{ error }}</template>
            <template v-else>
                На карте {{ total }} {{ plural(total, 'замер', 'замера', 'замеров') }}
                в {{ groups.length }} {{ plural(groups.length, 'месте', 'местах', 'местах') }}
                <template v-if="total > points.length"> (показаны первые {{ points.length }})</template>.
                Чем больше кружок, тем больше замеров снято в этом месте; зелёный — есть фото. Наведите на кружок, чтобы увидеть замеры.
            </template>
        </div>
        <l-map ref="map" style="min-height: 500px; width: 100%; z-index: 0" :zoom="zoom" :center="center">
            <l-tile-layer :url="url"></l-tile-layer>
            <l-control-layers />

            <l-circle-marker
                v-for="g in groups"
                :key="g.key"
                :lat-lng="[g.lat, g.lon]"
                :radius="radius(g.items.length)"
                :color="g.hasPhoto ? '#1B6E3C' : '#14508C'"
                :fill-color="g.hasPhoto ? '#1B6E3C' : '#14508C'"
                :fill-opacity="0.55"
                :weight="2"
            >
                <l-tooltip :options="{ direction: 'top' }">
                    <b>{{ g.items.length }} {{ plural(g.items.length, 'замер', 'замера', 'замеров') }}</b>
                    <template v-if="g.hasPhoto"> · есть фото</template>
                    <template v-for="m in g.items.slice(0, 6)">
                        <br :key="'br' + m.id">
                        <span :key="'m' + m.id">{{ m.type }}: {{ m.value === null || m.value === undefined ? '—' : m.value }} · {{ formatDate(m.date) }}</span>
                    </template>
                    <template v-if="g.items.length > 6"><br>и ещё {{ g.items.length - 6 }}</template>
                </l-tooltip>
            </l-circle-marker>
        </l-map>
    </div>
</template>

<script>
// Карта раньше рисовала только строки текущей страницы списка (10 из 1 576), а
// замеры одного места ложились меткой на метку — на карте оставалась одна точка.
// Теперь точки берутся лёгким вызовом /map/measurements/ (все видимые пользователю
// замеры с теми же отборами, что у таблицы), а замеры одного места собираются в
// один кружок: размер — число замеров, цвет — есть ли фото.
import {
  LMap,
  LTileLayer,
  LControlLayers,
  LTooltip,
  LCircleMarker,
} from 'vue2-leaflet'

import 'leaflet/dist/leaflet.css'
import api from '@/http/axios'

// Точность группировки: 4 знака после запятой — около 10 м, то есть «одно место».
const GROUP_DIGITS = 4
const PAGE_SIZE = 5000

export default {
  components: {
    LMap,
    LTileLayer,
    LControlLayers,
    LTooltip,
    LCircleMarker,
  },
  props: {
    // Строки текущей страницы больше не нужны карте; свойство оставлено, чтобы
    // не менять вызов в списке.
    data: {
      type: Array,
      default: () => [],
      required: false,
    },
    filters: {
      type: Object,
      default: () => ({}),
      required: false,
    },
  },
  data() {
    return {
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      zoom: 3,
      center: [60, 70],
      points: [],
      total: 0,
      loading: false,
      error: '',
      requestId: 0,
    }
  },
  computed: {
    groups() {
      const byPlace = new Map()
      for (const p of this.points) {
        if (p.lat === null || p.lat === undefined || p.lon === null || p.lon === undefined) continue
        const key = `${Number(p.lat).toFixed(GROUP_DIGITS)},${Number(p.lon).toFixed(GROUP_DIGITS)}`
        let g = byPlace.get(key)
        if (!g) {
          g = { key, lat: Number(p.lat), lon: Number(p.lon), items: [], hasPhoto: false }
          byPlace.set(key, g)
        }
        g.items.push(p)
        if (p.has_photo) g.hasPhoto = true
      }
      const list = [...byPlace.values()]
      for (const g of list) g.items.sort((a, b) => Number(b.date || 0) - Number(a.date || 0))
      return list
    },
  },
  watch: {
    filters: {
      handler() { this.load() },
      deep: true,
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    queryParams() {
      // В отбор идут только простые значения: так же их отдаёт в запрос таблица.
      const params = { size: PAGE_SIZE }
      for (const [k, v] of Object.entries(this.filters || {})) {
        if (v === null || v === undefined || v === '') continue
        if (typeof v === 'object') continue
        params[k] = v
      }
      return params
    },
    async load() {
      const id = ++this.requestId
      this.loading = true
      this.error = ''
      try {
        const { data } = await api.get('/map/measurements/', { params: this.queryParams(), timeout: 60000 })
        if (id !== this.requestId) return
        this.points = (data && data.results) || []
        this.total = (data && data.count) || this.points.length
        this.$nextTick(this.fitToPoints)
      } catch (e) {
        if (id !== this.requestId) return
        this.points = []
        this.total = 0
        this.error = 'Не удалось получить точки замеров с сервера — попробуйте обновить страницу.'
      } finally {
        if (id === this.requestId) this.loading = false
      }
    },
    fitToPoints() {
      const map = this.$refs.map && this.$refs.map.mapObject
      if (!map || !this.groups.length) return
      const lats = this.groups.map(g => g.lat)
      const lons = this.groups.map(g => g.lon)
      map.fitBounds([[Math.min(...lats), Math.min(...lons)], [Math.max(...lats), Math.max(...lons)]], { padding: [30, 30], maxZoom: 14 })
    },
    radius(n) {
      return Math.min(8 + Math.sqrt(n) * 3, 28)
    },
    formatDate(value) {
      if (!value) return 'дата не указана'
      // Сервер отдаёт дату замера отметкой времени в секундах строкой.
      const n = Number(value)
      const d = Number.isFinite(n) && String(value).length <= 11 ? new Date(n * 1000) : new Date(value)
      return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('ru-RU')
    },
    plural(n, one, few, many) {
      const m10 = n % 10
      const m100 = n % 100
      if (m10 === 1 && m100 !== 11) return one
      if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few
      return many
    },
  },
}
</script>
